"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Quote, Sparkles } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const narrativeBlocks = [{
  title: "Lligat al territori",
  body: "Salvat Serveis Rurals és un projecte vinculat al territori. Neix amb l’objectiu d’oferir serveis professionals per al manteniment de finques, terrenys, camins i espais de tot tipus. Coneixem les necessitats del territori i treballem per oferir solucions pràctiques i eficients.",
  image: "https://salvatserveisrurals.10web.cloud/wp-content/uploads/2026/03/rural_road_grading_work.webp?v=pie9r7y04xs",
  alt: "Treball de manteniment en una finca rural",
  quote: "La nostra missió és ajudar propietaris i empreses a mantenir les seves finques en bon estat amb professionalitat.",
  reverse: false
}, {
  title: "Respecte pel medi natural",
  body: "Treballem amb serietat, responsabilitat i respecte pel medi natural. Cada finca és diferent i adaptem cada treball a les seves necessitats, oferint serveis de desbrossament, poda i manteniment amb el màxim rigor i cura per l'entorn.",
  image: "https://salvatserveisrurals.10web.cloud/wp-content/uploads/2026/03/WhatsApp-Image-2026-03-13-at-14.14.22.jpeg?v=q7c1z79uxm",
  alt: "Maquinària agrícola treballant en la neteja de terrenys",
  quote: "Cada pam de terra mereix una cura específica i un manteniment que respecti el seu cicle natural.",
  reverse: true
}];
function RevealWrapper({
  children,
  className,
  delay = 0
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  return <div data-section-id="234013" ref={ref} className={cn("transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12", className)} style={{
    transitionDelay: `${delay}ms`
  }}>
      {children}
    </div>;
}
export default function MySacredMission() {
  return <section id="compromís-amb-la-terra" className="relative w-full text-foreground py-24 overflow-hidden bg-background !h-[2362px] md:py-32">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none -z-10">
        <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-primary/5 rounded-full blur-3xl mix-blend-multiply" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[60vw] h-[60vw] bg-secondary/10 rounded-full blur-3xl mix-blend-multiply" />
      </div>

      <div className="container mx-auto px-6 max-w-6xl !h-[2306px] md:px-12">
        {}
        <RevealWrapper className="flex flex-col items-center text-center mb-24">
          
          <h2 className="font-default text-4xl font-bold tracking-tight mb-6 !text-[hsl(74,42%,80%)] md:text-5xl lg:text-6xl">
            Compromís amb la Terra
          </h2>
          <p className="text-xl md:text-2xl text-foreground/90 max-w-2xl font-default leading-relaxed">
            Solucions professionals per al manteniment de finques, terrenys i camins amb un enfocament seriós i eficient.
          </p>
        </RevealWrapper>

        {}
        <div className="flex flex-col gap-32">
          {narrativeBlocks.map((block, index) => <div key={index} data-index={index} className="flex flex-col gap-24">
              {}
              <div className={cn("grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center")}>
                <RevealWrapper className={cn("order-2", block.reverse ? "lg:order-1" : "lg:order-2")}>
                  <div className="flex flex-col gap-6">
                    <h3 className="font-default text-3xl font-bold !text-[hsl(74,42%,80%)] md:text-4xl">
                      {block.title}
                    </h3>
                    <Separator className="w-12 h-1 bg-primary/40 rounded-full" />
                    <p className="text-lg leading-relaxed text-foreground/80 font-default">
                      {block.body}
                    </p>
                  </div>
                </RevealWrapper>

                <RevealWrapper delay={200} className={cn("order-1", block.reverse ? "lg:order-2" : "lg:order-1")}>
                  <div className="relative group">
                    <div className="absolute -inset-4 bg-primary/5 rounded-3xl blur-xl opacity-50 group-hover:opacity-70 transition-opacity duration-700" />
                    <AspectRatio ratio={4 / 5} className="relative z-10">
                      <img src={block.image} alt={block.alt} className="object-cover w-full h-full rounded-lg shadow-none border border-border" />
                      <div className="absolute inset-0 rounded-lg ring-1 ring-inset ring-primary/10 pointer-events-none" />
                    </AspectRatio>
                  </div>
                </RevealWrapper>
              </div>

              {}
              <RevealWrapper delay={100} className="w-full max-w-4xl mx-auto text-center px-4">
                <Quote className="w-12 h-12 mx-auto text-primary/20 mb-8 rotate-180" />
                <blockquote className="font-default italic text-2xl md:text-3xl lg:text-4xl leading-tight text-foreground/90">
                  "{block.quote}"
                </blockquote>
                <Separator className="w-24 mx-auto mt-12 bg-border" />
              </RevealWrapper>
            </div>)}
        </div>

        {}
        <RevealWrapper delay={300} className="mt-32 flex flex-col items-center text-center">
          
          <Button asChild size="lg" className="rounded-lg px-8 py-6 text-lg bg-primary text-primary-foreground hover:opacity-90 transition-all duration-300 shadow-none">
            
          </Button>
          <Separator className="w-[30%] mx-auto mt-24 bg-border" />
        </RevealWrapper>
      </div>
    </section>;
}